
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from '@/components/ui/chart';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Award, Package, ShoppingBag, TrendingUp, Users } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// Interfaces para os dados dos gráficos
interface SalesData {
  date: string;
  totalPoints: number;
  orderCount: number;
}

interface TopProductData {
  name: string;
  quantity: number;
  points: number;
}

interface TopUserData {
  name: string;
  totalPoints: number;
  orderCount: number;
}

const CHART_COLORS = [
  "#FF4560", "#00E396", "#008FFB", "#775DD0", "#FEB019", 
  "#FF9800", "#4CAF50", "#2196F3", "#E91E63", "#9C27B0"
];

const SalesDashboard: React.FC = () => {
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductData[]>([]);
  const [topUsers, setTopUsers] = useState<TopUserData[]>([]);
  const [totalStats, setTotalStats] = useState({
    totalOrders: 0,
    totalPoints: 0,
    totalUsers: 0,
    totalProducts: 0
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchSalesData(),
        fetchTopProducts(),
        fetchTopUsers(),
        fetchTotalStats()
      ]);
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar dados do dashboard',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados de vendas agrupados por data
  const fetchSalesData = async () => {
    const { data: orders, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Processar dados para o gráfico
    const salesByDate = orders!.reduce((acc: Record<string, SalesData>, order) => {
      const date = new Date(order.created_at).toISOString().split('T')[0];
      
      if (!acc[date]) {
        acc[date] = { date, totalPoints: 0, orderCount: 0 };
      }
      
      acc[date].totalPoints += order.points_used;
      acc[date].orderCount += 1;
      
      return acc;
    }, {});

    // Ordenar por data e pegar os últimos 30 dias
    const salesArray = Object.values(salesByDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-30);

    setSalesData(salesArray);
  };

  // Buscar produtos mais procurados
  const fetchTopProducts = async () => {
    // Buscar itens dos pedidos
    const { data: orderItems, error: orderItemsError } = await supabase
      .from('order_items')
      .select('product_id, quantity, points_price');

    if (orderItemsError) throw orderItemsError;

    if (!orderItems || orderItems.length === 0) {
      setTopProducts([]);
      return;
    }

    // Buscar informações de todos os produtos envolvidos
    const productIds = Array.from(new Set(orderItems.map(item => item.product_id)));
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, name')
      .in('id', productIds);

    if (productsError) throw productsError;

    // Criar um mapa de produtos para facilitar o acesso
    const productMap = products?.reduce((acc: Record<string, {name: string}>, product) => {
      acc[product.id] = { name: product.name };
      return acc;
    }, {}) || {};

    // Agrupar por produto e somar quantidade e pontos
    const productSales: Record<string, TopProductData> = {};

    orderItems.forEach(item => {
      const productId = item.product_id;
      const productName = productMap[productId]?.name || `Produto ${productId.slice(0, 6)}...`;
      
      if (!productSales[productId]) {
        productSales[productId] = {
          name: productName,
          quantity: 0,
          points: 0
        };
      }
      
      productSales[productId].quantity += item.quantity;
      productSales[productId].points += (item.quantity * item.points_price);
    });

    // Ordenar por quantidade e pegar os top 10
    const topProductsArray = Object.values(productSales)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    setTopProducts(topProductsArray);
  };

  // Buscar utilizadores com mais compras
  const fetchTopUsers = async () => {
    // Buscar pedidos com informações do utilizador
    const { data: orders, error } = await supabase
      .from('orders')
      .select(`
        points_used,
        user_id
      `);

    if (error) throw error;

    if (!orders || orders.length === 0) {
      setTopUsers([]);
      return;
    }

    // Buscar perfis de utilizadores
    const userIds = Array.from(new Set(orders.map(order => order.user_id)));
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username')
      .in('id', userIds);

    if (profilesError) throw profilesError;

    // Criar mapa de perfis
    const profileMap = profiles?.reduce((acc: Record<string, {username: string}>, profile) => {
      acc[profile.id] = { username: profile.username || 'Utilizador sem nome' };
      return acc;
    }, {}) || {};

    // Agrupar por utilizador e somar pontos
    const userMap: Record<string, TopUserData> = {};

    orders.forEach(order => {
      const userId = order.user_id;
      const username = profileMap[userId]?.username || `Utilizador ${userId.slice(0, 6)}...`;
      
      if (!userMap[userId]) {
        userMap[userId] = {
          name: username,
          totalPoints: 0,
          orderCount: 0
        };
      }
      
      userMap[userId].totalPoints += order.points_used;
      userMap[userId].orderCount += 1;
    });

    // Ordenar por pontos gastos e pegar os top 5
    const topUsersArray = Object.values(userMap)
      .sort((a, b) => b.totalPoints - a.totalPoints)
      .slice(0, 5);

    setTopUsers(topUsersArray);
  };

  // Buscar estatísticas gerais
  const fetchTotalStats = async () => {
    // Total de pedidos
    const { count: orderCount, error: orderError } = await supabase
      .from('orders')
      .select('id', { count: 'exact', head: true });

    if (orderError) throw orderError;

    // Total de pontos usados
    const { data: pointsData, error: pointsError } = await supabase
      .from('orders')
      .select('points_used');

    if (pointsError) throw pointsError;

    const totalPoints = pointsData!.reduce((sum, order) => sum + order.points_used, 0);

    // Total de utilizadores
    const { count: userCount, error: userError } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true });

    if (userError) throw userError;

    // Total de produtos
    const { count: productCount, error: productError } = await supabase
      .from('products')
      .select('id', { count: 'exact', head: true });

    if (productError) throw productError;

    setTotalStats({
      totalOrders: orderCount || 0,
      totalPoints,
      totalUsers: userCount || 0,
      totalProducts: productCount || 0
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  return (
    <div className="space-y-8 p-4">
      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white">
          <CardHeader className="pb-2">
            <CardDescription className="text-gray-400 flex items-center">
              <ShoppingBag size={16} className="mr-2" />
              Total de Pedidos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats.totalOrders}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white">
          <CardHeader className="pb-2">
            <CardDescription className="text-gray-400 flex items-center">
              <Award size={16} className="mr-2" />
              Pontos Utilizados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats.totalPoints}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white">
          <CardHeader className="pb-2">
            <CardDescription className="text-gray-400 flex items-center">
              <Users size={16} className="mr-2" />
              Utilizadores Registados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats.totalUsers}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-brand-darker to-brand-gray border-none text-white">
          <CardHeader className="pb-2">
            <CardDescription className="text-gray-400 flex items-center">
              <Package size={16} className="mr-2" />
              Produtos Registados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats.totalProducts}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Gráfico de vendas */}
      <Card className="bg-brand-gray border-none text-white mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp size={20} />
            Vendas por Período
          </CardTitle>
          <CardDescription className="text-gray-400">
            Pontos utilizados e quantidade de pedidos nos últimos 30 dias
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex justify-center h-[400px] items-center">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div>
            </div>
          ) : (
            <div className="h-[400px] w-full">
              <ChartContainer
                config={{
                  points: {
                    label: "Pontos",
                    color: "#FF4560"
                  },
                  orders: {
                    label: "Pedidos",
                    color: "#00E396"
                  },
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate} 
                      stroke="#888"
                    />
                    <YAxis yAxisId="left" stroke="#888" />
                    <YAxis yAxisId="right" orientation="right" stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#222', border: 'none', borderRadius: '8px' }} 
                      itemStyle={{ color: '#fff' }}
                      labelFormatter={(label) => `Data: ${formatDate(label)}`}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="totalPoints"
                      name="Pontos"
                      stroke="#FF4560"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orderCount"
                      name="Pedidos"
                      stroke="#00E396"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Grid de gráficos menores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Produtos mais procurados */}
        <Card className="bg-brand-gray border-none text-white">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Package size={20} />
              Produtos Mais Procurados
            </CardTitle>
            <CardDescription className="text-gray-400">
              Top 10 produtos com maior quantidade vendida
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {loading ? (
              <div className="flex justify-center h-[350px] items-center">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div>
              </div>
            ) : (
              <div className="h-[350px] w-full">
                <ChartContainer
                  config={{
                    quantity: {
                      label: "Quantidade",
                      color: "#008FFB"
                    }
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={topProducts} 
                      layout="vertical" 
                      barSize={20}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis type="number" stroke="#888" />
                      <YAxis 
                        dataKey="name" 
                        type="category"
                        width={120}
                        tick={{ fontSize: 12 }}
                        stroke="#888"
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#222', border: 'none', borderRadius: '8px' }} 
                        itemStyle={{ color: '#fff' }}
                      />
                      <Legend />
                      <Bar dataKey="quantity" name="Quantidade" fill="#008FFB" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Utilizadores com mais compras */}
        <Card className="bg-brand-gray border-none text-white">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Utilizadores com Mais Compras
            </CardTitle>
            <CardDescription className="text-gray-400">
              Top 5 utilizadores que mais utilizaram pontos
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {loading ? (
              <div className="flex justify-center h-[350px] items-center">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div>
              </div>
            ) : (
              <div className="h-[350px] w-full">
                <ChartContainer
                  config={{
                    points: {
                      label: "Pontos",
                      color: "#775DD0"
                    }
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topUsers}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="totalPoints"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {topUsers.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#222', border: 'none', borderRadius: '8px' }} 
                        itemStyle={{ color: '#fff' }}
                        formatter={(value, name, props) => [`${value} pontos`, props.payload.name]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesDashboard;
